'use client'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import classNames from 'classnames'
import {
  useOptimistic,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type TransitionStartFunction,
} from 'react'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import {
  useSiteContext,
  useTranslations,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import { getPrice } from '@dustin-web/microsite-data/src/utils'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'
import { ProductVariantKind } from '@productsite-beta/src/graphql/generated/graphql-types'
import { useProductOptimistic } from '../../../product-context/product-optimistic-context'
import ProductVariantSelector from '../product-variant-selector/product-variant-selector'
import ProductVariantImageSelector from '../product-variant-image-selector/product-variant-image-selector'
import { PRODUCT_PAGE_VARIANT_V2_PRICE_DIFFERENCE_FLAG } from '../../../product-page.constants'
import type { VariantValue } from './product-variant-group.types'

import './product-variant-group.scss'

const ExpandButton = dynamic(() => import('@dustin-web/microsite-components/src/expand-button'))

// tracks which group should restore focus after soft nav
let pendingFocusGroup: string | null = null

type Props = {
  variantName: string
  variantNameEng?: string | null
  variantKind: ProductVariantKind
  variantValues: VariantValue[]
  currentProductId: string
  isPending: boolean
  isActiveGroup?: boolean
  isActiveGroupImage?: boolean
  defaultExpanded?: boolean
  startTransition: TransitionStartFunction
  onNavigate: (groupName: string) => void
  onExpandChange?: (expanded: boolean) => void
  onMultiSpecSelect?: (value: VariantValue) => void
  variationKey: string | null | undefined
}

const ProductVariantGroup = ({
  variantName,
  variantKind,
  variantValues,
  currentProductId,
  isPending,
  isActiveGroup = false,
  isActiveGroupImage = false,
  defaultExpanded = false,
  startTransition,
  onNavigate,
  onExpandChange,
  onMultiSpecSelect,
  variationKey,
}: Props) => {
  const router = useRouter()
  const { sourcingCompany, isMobile } = useSiteContext()
  const { showPricesInclVat } = useUserSettings()
  const { setOptimistic, resetOptimistic } = useProductOptimistic()
  const { enabled: isShowPriceDifferenceEnabled } = useFeatureFlag(
    PRODUCT_PAGE_VARIANT_V2_PRICE_DIFFERENCE_FLAG
  )
  const [activeId, setOptimisticId] = useOptimistic(currentProductId)
  const { translate } = useTranslations()
  const listRef = useRef<HTMLDivElement>(null)

  const initalProductCount = isMobile ? 3 : 3

  const isImageVariant = variantKind === ProductVariantKind.ProductImage
  const headingId = useId()

  const availableVariants =
    variationKey == 'variant_c'
      ? variantValues
      : variantValues.filter(value => value.productVariantCombinationExists)

  const selectedIndex = availableVariants.findIndex(v => v.productErpIdentifier === activeId)
  const selectedBeyondCutoff = selectedIndex >= initalProductCount

  const [showAll, setshowAll] = useState(selectedBeyondCutoff || defaultExpanded)

  const variants =
    isImageVariant || showAll ? availableVariants : availableVariants.slice(0, initalProductCount)

  useEffect(() => {
    if (showAll) {
      onExpandChange?.(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll])

  useEffect(() => {
    if (!isPending) {
      resetOptimistic()

      if (pendingFocusGroup === variantName && listRef.current) {
        const selected = listRef.current.querySelector<HTMLElement>('[aria-checked="true"]')
        selected?.focus({ focusVisible: true } as FocusOptions)
        pendingFocusGroup = null
      }
    }
  }, [isPending, resetOptimistic, variantName])

  if (availableVariants.length === 1) {
    return null
  }

  const getWithoutVariant = (variantGroupName: string) =>
    `${translate('Variant_Without')} ${variantGroupName.toLocaleLowerCase()}`

  const selectedValue = variantValues.find(v => v.productErpIdentifier === activeId)

  const selectedLabel = selectedValue?.itemValueText
    ? `${selectedValue.itemValueText} ${selectedValue.unitText ?? ''}`
    : getWithoutVariant(variantName)

  const selectedPrice = selectedValue?.product?.price
    ? getPrice(selectedValue.product.price, showPricesInclVat)
    : null

  const showPrice = variantValues.some(
    v =>
      v.product?.availability?.isAvailableForSale &&
      v.product?.price?.priceExcludingVat !== selectedValue?.product?.price?.priceExcludingVat
  )

  const showRoundedPrice =
    sourcingCompany === 'SE' || sourcingCompany === 'NO' || sourcingCompany === 'DK'

  const handleSelect = (value: VariantValue) => {
    if (value.productErpIdentifier === activeId) {
      return
    }

    if (
      listRef.current?.contains(document.activeElement) &&
      document.activeElement?.matches(':focus-visible')
    ) {
      pendingFocusGroup = variantName
    }

    if (!value.productVariantCombinationExists) {
      onMultiSpecSelect?.(value)
      return
    }

    onNavigate(variantName)

    const price = value.product?.price

    setOptimistic({
      isNavigating: true,
      price: price ? (showPricesInclVat ? price.priceIncludingVat : price.priceExcludingVat) : null,
      campaign: price?.isCampaign ? { isCampaign: true, originalPrice: price.originalPrice } : null,
      displayName: value.product?.displayName ?? null,
      specs: value.product?.displaySpecificationsVariant ?? null,
      availability: value.product?.availability ?? null,
      primaryImageId:
        variantKind === ProductVariantKind.ProductImage
          ? (value.product?.primaryImageId ?? null)
          : null,
    })

    const href = getProductUrl(value.productErpIdentifier || '', value.product?.nameSlug || '')

    startTransition(() => {
      setOptimisticId(value.productErpIdentifier)
      router.push(href, { scroll: false })
    })
  }

  const toggleShowAll = () => {
    setshowAll(state => {
      const next = !state
      onExpandChange?.(next)
      return next
    })
  }

  return (
    <div
      className={classNames('product-variant-group', {
        loading: isPending && !(isActiveGroupImage && isImageVariant),
      })}>
      <h2 id={headingId}>
        <span>{variantName}: </span>
        <strong>{selectedLabel}</strong>
      </h2>

      <VariantListWrapper
        isImageVariant={isImageVariant}
        isPending={isPending}
        headingId={headingId}
        listRef={listRef}>
        {variants.map((variantValue, index) => {
          const { productErpIdentifier, itemValueText, unitText, product } = variantValue ?? {}

          const displayName = itemValueText
            ? `${itemValueText} ${unitText ?? ''}`
            : getWithoutVariant(variantName)

          const isSelected = productErpIdentifier === activeId

          const href = getProductUrl(
            variantValue.productErpIdentifier || '',
            variantValue.product?.nameSlug || ''
          )

          if (isImageVariant) {
            return (
              <ProductVariantImageSelector
                key={productErpIdentifier}
                index={index}
                isSelected={isSelected}
                href={getProductUrl(productErpIdentifier || '', product?.nameSlug || '')}
                variantPrimaryImageId={product?.primaryImageId ?? ''}
                variantDisplayName={product?.displayName ?? ''}
                variantColorName={itemValueText ?? ''}
                onButtonClick={() => handleSelect(variantValue)}
              />
            )
          }

          return (
            <ProductVariantSelector
              key={productErpIdentifier}
              variantName={displayName}
              href={href}
              showPrice={showPrice}
              showPricesInclVat={showPricesInclVat}
              showRoundedPrice={showRoundedPrice}
              showPriceDifference={isShowPriceDifferenceEnabled}
              affectsMultipleSpecs={!variantValue.productVariantCombinationExists}
              selectedPrice={selectedPrice}
              price={product?.price}
              isSelected={isSelected}
              isLoading={isPending && isActiveGroup && isSelected}
              affectsMultipleSpecsText={translate('Product_Variant_Affects_Multiple_Specs')}
              onSelect={() => handleSelect(variantValue)}
            />
          )
        })}
      </VariantListWrapper>
      {!isImageVariant && availableVariants?.length > initalProductCount && (
        <ExpandButton
          className="u-pt-12"
          onClick={toggleShowAll}
          collapsedTitle={translate('ShowMore')}
          expandedTitle={translate('ShowLess')}
          expanded={showAll}
          ariaControls="expandable-highlights-cards"
        />
      )}
    </div>
  )
}

const VariantListWrapper = ({
  isImageVariant,
  isPending,
  headingId,
  listRef,
  children,
}: {
  isImageVariant: boolean
  isPending: boolean
  headingId: string
  listRef: React.RefObject<HTMLDivElement | null>
  children: ReactNode
}) => {
  const props = {
    ref: listRef,
    role: 'radiogroup' as const,
    'aria-labelledby': headingId,
    'aria-busy': isPending || undefined,
  }

  return isImageVariant ? (
    <div className="product-variant-image-group" {...props}>
      {children}
    </div>
  ) : (
    <div className="product-variant-selector-group" {...props}>
      {children}
    </div>
  )
}

export default ProductVariantGroup
